import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Tooltip } from '@nextui-org/react'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useMemo, useState, type ReactElement } from 'react'
import { MdDownload, MdRoute, MdWarehouse, MdLocalShipping, MdFileDownload, MdList, MdWarning } from 'react-icons/md' // MdRefresh
import { useSelector } from 'react-redux'
import ApiStateView from '../ApiState'
// import KpiCard from '../KpiCard'
import AddressModal from '../AddressModal'
import CajasModal from '../CajasModal'
import UploadLabelModal from '../UploadLabelModal'
import LogiSearch from '../LogiSearch'
import LogiTable from '../LogiTable'
// import SectionTitle from '../SectionTitle'
import { TONES, COURIER, COUNTRY, TERMINO_ETIQUETA, TERMINO_ETIQUETA_LOWER } from '../../constants'
import { labelsApi, resolveLabelUrl, fileToBase64, LabelTodayEstado, LabelTodayRow, StoreSinDireccion, StoreAddressInput } from '../../api'
import { useApi } from '../../hooks/useApi'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useStoreScope } from '../../hooks/useStoreScope'
import { IAppStore } from '../../../../../models/store.model'
// import { KpiCardData, ToneKey } from '../../models'

// Chip de estado de la tabla principal. Para etiquetas con tracking activo
// (estado='lista') muestra el avance real: Creada → Retiro → En camino → Entregada.
// 'error' se mantiene; 'generando'/'pendiente' son defensivos (no deberían verse).
const StatusChip: FC<{ estado: LabelTodayEstado, estadoLocal: LabelTodayRow['estado_local'] }> = ({ estado, estadoLocal }) => {
  if (estado === 'error') return <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FAECE7] text-[#993C1D]'>! Con error</span>
  if (estado === 'generando') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]'>
        <span className='w-1.5 h-1.5 rounded-full animate-pulse ml-1' style={{ background: TONES.amber.ink }} />
        Generando
      </span>
    )
  }
  if (estado === 'pendiente') return <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#ede9fe] text-[#7c3aed]'>● Pendiente</span>
  // estado === 'lista' → avance del tracking
  if (estadoLocal === 'entregada') return <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#EAF3DE] text-[#3B6D11]'>● Entregada</span>
  if (estadoLocal === 'en_transito') return <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E6F1FB] text-[#185FA5]'>● En camino</span>
  if (estadoLocal === 'recogida') return <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E0F2F1] text-[#0F766E]'>● Retiro</span>
  return <span className='inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]'>● Creada</span>
}

// Formatea la fecha de recolección · '—' si viene null/undefined o no es parseable.
const fmtFechaPickup = (iso: string | null | undefined): string => {
  if (iso === null || iso === undefined || iso === '') return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

const columns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Encargado', uid: 'encargado' },
  { name: 'Estado', uid: 'estado' },
  { name: 'Tipo de retiro', uid: 'tipo_retiro', align: 'center' as const },
  { name: 'Fecha recolección', uid: 'fecha_recoleccion', sortBy: (e: LabelTodayRow) => e.fecha_programada_pickup },
  { name: 'Tracking #', uid: 'tracking', sortBy: (e: LabelTodayRow) => e.tracking_number },
  { name: 'Referencia', uid: 'referencia', sortBy: (e: LabelTodayRow) => e.referencia },
  // Solicitud BX (pickup Blue Express) es exclusivo de Chile.
  ...(COUNTRY === 'CL' ? [{ name: 'Solicitud BX', uid: 'pickup', sortBy: (e: LabelTodayRow) => e.pickup_id }] : []),
  { name: 'Sellos', uid: 'sellos', sortBy: (e: LabelTodayRow) => e.seal },
  { name: TERMINO_ETIQUETA, uid: 'etiqueta', sortable: false },
  { name: 'Generada', uid: 'generada', sortBy: (e: LabelTodayRow) => e.generada_a },
  { name: '', uid: 'actions' }
]

// Columnas de la tabla de Errores (exclusiva de recolecciones que fallaron).
const errorColumns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Encargado', uid: 'encargado' },
  { name: 'Motivo del error', uid: 'motivo', sortable: false },
  { name: 'Detectado', uid: 'generada', sortBy: (e: LabelTodayRow) => e.generada_a },
  { name: '', uid: 'actions' }
]

// Columnas de la tabla de tiendas sin dirección (no se pudo crear la recolección).
const sinDirColumns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'En custodia', uid: 'en_custodia', align: 'center' as const },
  { name: 'Motivo', uid: 'motivo', sortable: false },
  { name: '', uid: 'actions' }
]

// Traduce el mensaje de error técnico a un motivo entendible + acción sugerida.
const motivoError = (msg: string | null): string => {
  const m = (msg ?? '').toLowerCase()
  if (m.includes('direcc') || m.includes('address') || m.includes('postal') || m.includes('codigo postal')) {
    return 'Dirección inválida o faltante · revisar la dirección de la tienda y reintentar'
  }
  if (m.includes('contacto') || m.includes('phone') || m.includes('teléfono') || m.includes('telefono')) {
    return 'Datos de contacto faltantes · completar contacto de la tienda y reintentar'
  }
  if (m.includes('dhl')) return 'Falla del servicio DHL · reintentar'
  return msg ?? `Error al generar la ${TERMINO_ETIQUETA_LOWER} · reintentar`
}

const EtiquetasView: FC = () => {
  const userId = useSelector((s: IAppStore) => s.user?.id ?? null)
  const idSeller = useSelectedSeller()
  const { goToEnvio, goToTrackingDetail, setActiveView } = useLogisticBox()
  const { enqueueSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'principal' | 'errores'>('principal')
  // const [retryingAll, setRetryingAll] = useState(false)
  const [regenerating, setRegenerating] = useState<number | null>(null)
  // Modal de previsualización de la etiqueta PDF
  const [pdfView, setPdfView] = useState<{ url: string, store: string } | null>(null)

  // Jefe de tienda → solo su tienda · admin → todo el seller (scopeStore null).
  const { scopeStore } = useStoreScope()
  // Acciones/tabs de admin ocultas en la app de tiendas (viven en arrivals).
  const MOSTRAR_ADMIN_RECOLECCIONES = false
  const fetchToday = useCallback(async () => await labelsApi.today(idSeller, scopeStore), [idSeller, scopeStore])
  const labels = useApi<LabelTodayRow[]>(fetchToday, [idSeller, scopeStore])

  // La tabla principal muestra solo recolecciones con etiqueta lista (Creada/Retiro/
  // En camino). Se ocultan 'pendiente' y 'error' (las con error viven en "No creadas"),
  // y las 'entregada' por ahora (quitar el `estado_local !== 'entregada'` para mostrarlas).
  const visibleEnPrincipal = (e: LabelTodayRow): boolean =>
    e.estado !== 'pendiente' && e.estado !== 'error' && e.estado_local !== 'entregada'

  const rows = useMemo(() => {
    const all = (labels.data ?? []).filter(visibleEnPrincipal)
    if (search === '') return all
    const q = search.toLowerCase()
    return all.filter(e =>
      `${e.store} ${e.encargado ?? ''} ${e.tracking_number ?? ''} ${e.referencia ?? ''} ${e.pickup_id ?? ''}`.toLowerCase().includes(q)
    )
  }, [labels.data, search])

  // Total de filas con recolección (denominador del contador inferior)
  const totalConEtiqueta = useMemo(
    () => (labels.data ?? []).filter(visibleEnPrincipal).length,
    [labels.data]
  )

  // Tabla exclusiva de Errores · recolecciones cuya etiqueta no se pudo generar.
  const errorRows = useMemo(() => {
    const all = (labels.data ?? []).filter(e => e.estado === 'error')
    if (search === '') return all
    const q = search.toLowerCase()
    return all.filter(e => `${e.store} ${e.encargado ?? ''}`.toLowerCase().includes(q))
  }, [labels.data, search])

  const totalErrores = useMemo(
    () => (labels.data ?? []).filter(e => e.estado === 'error').length,
    [labels.data]
  )

  // Tiendas con custodia sin dirección (no se pudo crear la recolección).
  const sinDir = useApi<StoreSinDireccion[]>(
    useCallback(async () => await labelsApi.sinDireccion(idSeller), [idSeller]),
    [idSeller]
  )
  const sinDirRows = useMemo(() => {
    const all = sinDir.data ?? []
    if (search === '') return all
    const q = search.toLowerCase()
    return all.filter(t => t.store.toLowerCase().includes(q))
  }, [sinDir.data, search])
  const totalSinDir = sinDir.data?.length ?? 0
  // Conteo total del tab "No generadas" = errores DHL + sin dirección.
  const totalNoGeneradas = totalErrores + totalSinDir

  // Modal "Agregar dirección"
  const [addrTarget, setAddrTarget] = useState<{ id_store: number, store: string } | null>(null)
  // Carga manual de etiqueta (PDF) cuando falla DHL
  // Modal "Cargar etiqueta" · pide tracking DHL + PDF
  const [uploadTarget, setUploadTarget] = useState<{ id_collection: number, id_store: number, store: string } | null>(null)
  const [cajasTarget, setCajasTarget] = useState<{ id_collection: number, id_store: number, store: string } | null>(null)
  const [uploading, setUploading] = useState(false)

  const handleUploadLabel = async (tracking: string, file: File): Promise<void> => {
    if (uploadTarget === null) return
    setUploading(true)
    try {
      const b64 = await fileToBase64(file)
      const r = await labelsApi.uploadManualLabel(uploadTarget.id_collection, uploadTarget.id_store, b64, tracking, idSeller, userId === 0 ? null : userId)
      enqueueSnackbar(`${TERMINO_ETIQUETA} cargada (${r.tracking_number})`, { variant: 'success' })
      setUploadTarget(null)
      await labels.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? `No se pudo cargar la ${TERMINO_ETIQUETA_LOWER}`, { variant: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleSaveAddress = async (input: StoreAddressInput): Promise<void> => {
    try {
      const r = await labelsApi.upsertAddress(input, idSeller, userId === 0 ? null : userId)
      const gen = r.generacion
      if (gen?.triggered === true) {
        enqueueSnackbar(`Dirección guardada · recolección generada${gen.tracking_number !== null ? ` (folio ${gen.tracking_number})` : ''}`, { variant: 'success' })
      } else {
        enqueueSnackbar(`Dirección guardada · ${gen?.motivo_no_trigger ?? 'la recolección se generará luego'}`, { variant: 'warning' })
      }
      setAddrTarget(null)
      await Promise.all([sinDir.reload(), labels.reload()])
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo guardar la dirección', { variant: 'error' })
    }
  }

  // const kpis: KpiCardData[] = useMemo(() => {
  //   const all = labels.data ?? []
  //   const listas = all.filter(e => e.estado === 'lista').length
  //   const errores = all.filter(e => e.estado === 'error').length
  //   const denom = listas + errores
  //   const pctExito = denom === 0 ? 100 : Math.round((listas / denom) * 100)
  //   return [
  //     { value: listas, label: 'Listas', tone: 'green' as ToneKey, hint: 'Etiqueta generada · tracking activo' },
  //     { value: `${pctExito}%`, label: 'Porcentaje de éxito', tone: 'blue' as ToneKey, hint: `${listas} listas · ${errores} error` },
  //     { value: errores, label: 'Con error', tone: 'rosa' as ToneKey, hint: 'Reintentos automáticos: 3 máx' }
  //   ]
  // }, [labels.data])

  // const handleRetryAll = async (): Promise<void> => {
  //   setRetryingAll(true)
  //   try {
  //     const r = await labelsApi.retryPending(idSeller, userId === 0 ? null : userId)
  //     enqueueSnackbar(
  //       `${r.exitosas} exitosas / ${r.fallidas} fallidas de ${r.intentadas}`,
  //       { variant: r.fallidas === 0 ? 'success' : 'warning' }
  //     )
  //     await labels.reload()
  //   } catch (e: any) {
  //     enqueueSnackbar(e?.message ?? 'No se pudo reintentar', { variant: 'error' })
  //   } finally {
  //     setRetryingAll(false)
  //   }
  // }

  const handleRegenerate = async (idCollection: number, tipoRetiro: 'dhl' | 'interno' = 'dhl'): Promise<void> => {
    setRegenerating(idCollection)
    try {
      const r = await labelsApi.regenerate(idCollection, idSeller, userId === 0 ? null : userId, tipoRetiro)
      if (r.success) {
        enqueueSnackbar(
          tipoRetiro === 'interno'
            ? `${TERMINO_ETIQUETA} Reuse generada (folio ${r.tracking_number ?? '?'})`
            : `${TERMINO_ETIQUETA} generada (tracking ${r.tracking_number ?? '?'})`,
          { variant: 'success' }
        )
      } else {
        enqueueSnackbar(r.error_message ?? 'No se pudo generar', { variant: 'error' })
      }
      await labels.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo generar', { variant: 'error' })
    } finally {
      setRegenerating(null)
    }
  }

  const handleLabelsRetry = (): void => { labels.reload() }
  const handleBothRetry = (): void => { labels.reload(); sinDir.reload() }

  const handleExportCsv = useCallback(() => {
    const headers = ['ID', 'Tienda', 'Encargado', 'Estado', 'Tipo de retiro', 'Tracking #', 'Etiqueta URL', 'Generada']
    const data = rows.map(r => [
      r.id_store,
      r.store,
      r.encargado ?? '',
      r.estado,
      r.tipo_retiro === 'interno' ? 'Retiro Reuse' : (r.tipo_retiro !== null ? COURIER : ''),
      r.tracking_number ?? '',
      r.pdf_url ?? '',
      r.generada_a ?? ''
    ])

    const escapeCell = (v: any) => {
      if (v === null || v === undefined) return ''
      const s = String(v)
      return '"' + s.replace(/"/g, '""') + '"'
    }

    const csvContent = [headers, ...data].map(row => row.map(escapeCell).join(',')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `etiquetas_${new Date().toISOString().slice(0, 10)}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [rows])

  const renderCell = (e: LabelTodayRow, columnKey: string): ReactElement => {
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-xs'>{e.store}</span>
      case 'encargado':
        return <span className='text-xs'>{e.encargado ?? '—'}</span>
      case 'estado':
        return <StatusChip estado={e.estado} estadoLocal={e.estado_local} />
      case 'tipo_retiro':
        if (e.tipo_retiro === 'interno') {
          return <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#ede9fe] text-[#7c3aed]'><MdWarehouse className='text-sm' />Retiro Reuse</span>
        }
        if (e.tipo_retiro !== null && e.tipo_retiro !== undefined) {
          return <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E6F1FB] text-[#185FA5]'><MdLocalShipping className='text-sm' />{COURIER}</span>
        }
        return <span className='text-xs text-gray-400'>—</span>
      case 'tracking':
        return (
          <span className='text-xs text-neutral-700'>
            {e.tracking_number ?? <span className='text-gray-400'>esperando…</span>}
          </span>
        )
      case 'referencia':
        return (
          <span className='text-xs font-mono text-neutral-700'>
            {e.referencia ?? <span className='text-gray-400'>—</span>}
          </span>
        )
      case 'pickup':
        return (
          <span className='text-xs text-neutral-700'>
            {e.pickup_id ?? <span className='text-gray-400'>—</span>}
          </span>
        )
      case 'sellos':
        return (
          <span className='text-xs text-neutral-700'>
            {e.seal !== null ? `${e.seal}${e.seal2 !== null ? ' / ' + e.seal2 : ''}` : <span className='text-gray-400'>sin sellar</span>}
          </span>
        )
      case 'etiqueta':
        return (
          <span className='text-xs'>
            {e.estado === 'lista' && resolveLabelUrl(e.pdf_url, idSeller) !== null && (
              <a
                className='text-[#185FA5] font-medium hover:underline text-xs inline-flex items-center gap-1'
                href={resolveLabelUrl(e.pdf_url, idSeller) as string}
                target='_blank'
                rel='noreferrer'
              >
                <MdDownload /> Descargar PDF
              </a>
            )}
            {e.estado === 'generando' && (
              <div className='inline-flex items-center gap-2 text-xs text-gray-500'>
                <div className='w-20 h-1.5 rounded-full overflow-hidden' style={{ background: TONES.amber.bg }}>
                  <div className='h-full animate-pulse' style={{ width: '60%', background: TONES.amber.ink }} />
                </div>
                <span>en proceso</span>
              </div>
            )}
            {e.estado === 'error' && (
              <span className='text-xs text-rose-500'>{e.error_message ?? 'error'}</span>
            )}
            {e.estado === 'pendiente' && <span className='text-xs text-gray-400'>en cola</span>}
          </span>
        )
      case 'fecha_recoleccion':
        return <span className='text-xs text-neutral-800/80'>{fmtFechaPickup(e.fecha_programada_pickup)}</span>
      case 'generada':
        return (
          <span className='text-xs text-neutral-800/60'>
            {e.generada_a !== null ? fmtFechaPickup(e.generada_a) : (e.estado === 'generando' ? 'en proceso' : '—')}
          </span>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            {MOSTRAR_ADMIN_RECOLECCIONES && e.estado === 'error' && e.id_collection !== null
              ? (
                <>
                  <Button
                    size='sm'
                    className='bg-[#44526B] text-white text-xs h-8'
                    isLoading={regenerating === e.id_collection}
                    onPress={() => { handleRegenerate(e.id_collection as number) }}
                  >
                    Regenerar
                  </Button>
                  <Button
                    size='sm'
                    variant='bordered'
                    className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                    isDisabled={regenerating === e.id_collection}
                    onPress={() => { handleRegenerate(e.id_collection as number, 'interno') }}
                  >
                    Retiro interno
                  </Button>
                  <Button
                    size='sm' variant='bordered' className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                    onPress={() => { if (e.id_collection !== null) setUploadTarget({ id_collection: e.id_collection, id_store: e.id_store, store: e.store }) }}
                  >
                    Cargar {TERMINO_ETIQUETA_LOWER}
                  </Button>
                </>
                )
              : (
                <Button
                  size='sm'
                  className='bg-[#44526B] text-white text-xs h-8'
                  isDisabled={e.pdf_url === null}
                  onPress={() => { const u = resolveLabelUrl(e.pdf_url, idSeller); if (u !== null) setPdfView({ url: u, store: e.store }) }}
                >
                  Ver {TERMINO_ETIQUETA_LOWER}
                </Button>
                )}
            {e.tracking_number !== null && (
              <Button
                size='sm'
                variant='bordered'
                className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                onPress={() => { goToTrackingDetail(e.tracking_number as string) }}
              >
                Ver tracking
              </Button>
            )}
            {e.id_collection !== null && (
              <Button
                size='sm'
                variant='bordered'
                className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                onPress={() => { goToEnvio(e.id_collection as number, e.store, e.seal, e.seal2, e.box_key, e.tracking_number) }}
              >
                Ver envío
              </Button>
            )}
            {MOSTRAR_ADMIN_RECOLECCIONES && e.id_collection !== null && (
              <Button
                size='sm'
                variant='bordered'
                className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                onPress={() => { setCajasTarget({ id_collection: e.id_collection as number, id_store: e.id_store, store: e.store }) }}
              >
                Cajas
              </Button>
            )}
          </div>
        )
      case 'motivo':
        return <span className='text-xs text-[#993C1D]'>{motivoError(e.error_message)}</span>
      default:
        return <span className='text-xs'>{(e as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      {/* <section className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {kpis.map((k, i) => <KpiCard key={i} data={k} />)}
      </section> */}

      <section className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
        <div className='flex'>
          {/* Sidebar de tabs */}
          <aside className='w-[190px] flex-shrink-0 flex flex-col gap-1 p-3 border-r border-neutral-100'>
            <Tooltip content='Principal' placement='right' size='sm'>
              <button
                type='button'
                onClick={() => setTab('principal')}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${tab === 'principal' ? 'bg-[#44526B] text-white font-semibold' : 'text-neutral-500 hover:bg-neutral-100 font-medium'}`}
              >
                <span className='flex items-center gap-2 min-w-0'>
                  <MdList className='text-base shrink-0' />
                  <span className='truncate'>Principal</span>
                </span>
                <span className={`shrink-0 text-[12px] leading-none px-1.5 py-0.5 rounded-full font-semibold ${tab === 'principal' ? 'bg-white/20 text-white' : 'bg-neutral-200/70 text-neutral-500'}`}>
                  {totalConEtiqueta}
                </span>
              </button>
            </Tooltip>
            {MOSTRAR_ADMIN_RECOLECCIONES && (
              <Tooltip content='No creadas' placement='right' size='sm'>
                <button
                  type='button'
                  onClick={() => setTab('errores')}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${tab === 'errores' ? 'bg-[#44526B] text-white font-semibold' : 'text-neutral-500 hover:bg-neutral-100 font-medium'}`}
                >
                  <span className='flex items-center gap-2 min-w-0'>
                    <MdWarning className='text-base shrink-0' />
                    <span className='truncate'>No creadas</span>
                  </span>
                  <span className={`shrink-0 text-[12px] leading-none px-1.5 py-0.5 rounded-full font-semibold ${tab === 'errores' ? 'bg-white/20 text-white' : totalNoGeneradas > 0 ? 'bg-[#FAECE7] text-[#993C1D]' : 'bg-neutral-200/70 text-neutral-500'}`}>
                    {totalNoGeneradas}
                  </span>
                </button>
              </Tooltip>
            )}
          </aside>

          {/* Contenido del tab activo */}
          <div className='flex-1 min-w-0'>
            <div className='flex items-center justify-between flex-wrap gap-3 px-5 pt-5 pb-3'>
              <LogiSearch className='max-w-sm' placeholder='Buscar por tienda, referencia, tracking # o encargado…' value={search} onChange={setSearch} />
              <div className='flex items-center gap-2 flex-wrap'>
                {MOSTRAR_ADMIN_RECOLECCIONES && (
                  <Button
                    size='sm'
                    className='bg-[#44526B] text-white text-xs h-9'
                    startContent={<MdRoute className='text-base' />}
                    onPress={() => setActiveView('recolecciones-internas')}
                  >
                    Recolecciones Reuse
                  </Button>
                )}
                <Button variant='bordered' className='border-gray-200 text-neutral-800 text-xs h-9' startContent={<MdFileDownload className='text-base' />} onPress={() => { handleExportCsv() }}>Exportar</Button>
              </div>
            </div>

            <div className='px-5 pb-5'>
              {tab === 'principal'
                ? (
                  <ApiStateView
                    loading={labels.loading}
                    error={labels.error}
                    onRetry={handleLabelsRetry}
                    empty={!labels.loading && rows.length === 0}
                    emptyText='Sin recolecciones para mostrar'
                  >
                    <LogiTable
                      ariaLabel='Etiquetas por tienda'
                      columns={columns}
                      items={rows}
                      renderCell={renderCell}
                      rowKey={(e) => e.id_collection ?? e.id_store}
                      loading={labels.loading}
                      emptyText='Sin recolecciones para mostrar'
                      rowsPerPage={10}
                      bottomExtra={
                        <div className='flex items-center justify-between text-xs text-neutral-800/60 flex-wrap gap-3'>
                          <span>Mostrando {rows.length} de {totalConEtiqueta} recolecciones</span>
                        </div>
                      }
                    />
                  </ApiStateView>
                  )
                : (
                  <ApiStateView
                    loading={labels.loading || sinDir.loading}
                    error={labels.error ?? sinDir.error}
                    onRetry={handleBothRetry}
                    empty={!labels.loading && !sinDir.loading && errorRows.length === 0 && sinDirRows.length === 0}
                    emptyText='Nada por resolver · todas las recolecciones se generaron'
                  >
                    <div className='space-y-5'>
                      {sinDirRows.length > 0 && (
                        <div>
                          <h3 className='text-xs font-bold uppercase tracking-wide text-neutral-800/55 mb-2'>
                            Sin dirección ({sinDirRows.length})
                          </h3>
                          <LogiTable
                            ariaLabel='Tiendas sin dirección'
                            columns={sinDirColumns}
                            items={sinDirRows}
                            rowKey={(t) => `sd-${t.id_store}`}
                            loading={sinDir.loading}
                            emptyText='Sin tiendas en esta vista'
                            rowsPerPage={10}
                            renderCell={(t, key) => {
                              switch (key) {
                                case 'store':
                                  return <span className='font-medium text-xs text-neutral-800'>{t.store}</span>
                                case 'en_custodia':
                                  return <span className='text-xs font-semibold'>{t.en_custodia}</span>
                                case 'motivo':
                                  return <span className='text-xs text-[#993C1D]'>Sin dirección · no se puede generar la recolección</span>
                                case 'actions':
                                  return (
                                    <div className='flex justify-end'>
                                      <Button
                                        size='sm' className='bg-[#44526B] text-white text-xs h-8'
                                        onPress={() => setAddrTarget({ id_store: t.id_store, store: t.store })}
                                      >
                                        Agregar dirección
                                      </Button>
                                    </div>
                                  )
                                default:
                                  return <span className='text-xs'>{(t as any)[key]}</span>
                              }
                            }}
                          />
                        </div>
                      )}
                      {errorRows.length > 0 && (
                        <div>
                          <h3 className='text-xs font-bold uppercase tracking-wide text-neutral-800/55 mb-2'>
                            Errores de {TERMINO_ETIQUETA_LOWER} ({errorRows.length})
                          </h3>
                          <LogiTable
                            ariaLabel='Recolecciones con error'
                            columns={errorColumns}
                            items={errorRows}
                            renderCell={renderCell}
                            rowKey={(e) => `err-${e.id_collection ?? e.id_store}`}
                            loading={labels.loading}
                            emptyText='Sin errores'
                            rowsPerPage={10}
                            bottomExtra={
                              <div className='text-xs text-neutral-800/60'>
                                {errorRows.length} recolección(es) con error · reintentar tras corregir la causa
                              </div>
                            }
                          />
                        </div>
                      )}
                    </div>
                  </ApiStateView>
                  )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal · agregar dirección a una tienda sin dirección */}
      <AddressModal target={addrTarget} onClose={() => setAddrTarget(null)} onSave={handleSaveAddress} confirmLabel={`Crear ${TERMINO_ETIQUETA_LOWER}`} />

      {/* Modal · etiquetas por caja (par de sellos) de una recolección */}
      <CajasModal
        target={cajasTarget}
        idSeller={idSeller}
        userId={userId === 0 ? null : userId}
        onClose={() => setCajasTarget(null)}
      />

      {/* Modal · cargar etiqueta DHL manual (tracking + PDF) */}
      <UploadLabelModal
        open={uploadTarget !== null}
        store={uploadTarget?.store}
        busy={uploading}
        onClose={() => setUploadTarget(null)}
        onSubmit={(tracking, file) => { handleUploadLabel(tracking, file) }}
      />

      {/* Modal de previsualización de la etiqueta PDF */}
      <Modal isOpen={pdfView !== null} onClose={() => setPdfView(null)} size='3xl' scrollBehavior='inside'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-neutral-600/60 font-bold'>{TERMINO_ETIQUETA} {COURIER}</span>
            <span className='text-base font-bold text-neutral-800'>{pdfView?.store}</span>
          </ModalHeader>
          <ModalBody className='p-0'>
            {pdfView !== null && (
              <iframe
                title={`${TERMINO_ETIQUETA} ${COURIER}`}
                src={pdfView.url}
                className='w-full'
                style={{ height: '70vh', border: 'none' }}
              />
            )}
          </ModalBody>
          <ModalFooter className='flex justify-end gap-2'>
            <Button variant='bordered' className='border-gray-200 text-neutral-800' onPress={() => setPdfView(null)}>
              Cerrar
            </Button>
            {pdfView !== null && (
              <Button as='a' href={pdfView.url} target='_blank' rel='noreferrer' className='bg-[#44526B] text-white' endContent={<MdDownload />}>
                Descargar
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default EtiquetasView
